import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';

import { SessionController } from 'src/app/auth/session/session-controller';

export const LOGIN_ROUTE = '/login';
export const DASHBOARD_ROUTE = '/dashboard';

/**
 * Protects every route except the login page.
 *
 * The decision is SessionController.isAuthenticated, the predicate NoAuthGuard applies on the
 * other side. A refused navigation is answered with the login page as a redirect (a UrlTree),
 * with the refused target as `returnUrl` so the login page can bring the user there afterwards.
 */
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild {
  constructor(
    private _session: SessionController,
    private _router: Router
  ) {}

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return this.check(state.url);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return this.check(state.url);
  }

  private async check(targetUrl: string): Promise<boolean | UrlTree> {
    await this._session.ready;
    if (this._session.isAuthenticated) {
      // the web sign-in comes back at the origin; the target of the deep link travelled with it
      const target = this._session.consumePostLoginTarget();
      return target && target !== targetUrl ? this._router.parseUrl(target) : true;
    }
    const worthRemembering = targetUrl && targetUrl !== '/' && !targetUrl.startsWith(LOGIN_ROUTE);
    return this._router.createUrlTree([LOGIN_ROUTE], { queryParams: worthRemembering ? { returnUrl: targetUrl } : {} });
  }
}
